import type { Artifact, ChatMessage, Conversation } from "./types";
import { normalizeQuestionMode } from "./defaults";
import { isChatModeId } from "./modes";

/** Payload carried inside a `#share=` link. */
interface SharePayload {
  conversation: Conversation;
  artifacts: Artifact[];
}

export interface SharedImport {
  conversation: Conversation;
  artifacts: Artifact[];
}

export function truncateBranchAfter(
  messages: ChatMessage[],
  messageId: string,
): ChatMessage[] {
  const index = messages.findIndex((message) => message.id === messageId);
  if (index < 0) return messages;
  const branchId = messages[index]!.branchId;
  return messages.filter(
    (message, messageIndex) => message.branchId !== branchId || messageIndex <= index,
  );
}

export function cloneBranchAt(
  conversation: Conversation,
  messageId: string,
  createId: () => string = () => crypto.randomUUID(),
): Conversation {
  const target = conversation.messages.find((message) => message.id === messageId);
  if (!target) return conversation;
  const source = conversation.messages.filter(
    (message) => message.branchId === target.branchId,
  );
  const index = source.findIndex((message) => message.id === messageId);
  const branchId = createId();
  const idMap = new Map<string, string>();
  const cloned = source.slice(0, index + 1).map((message) => {
    const { parentId, ...rest } = message;
    const id = createId();
    idMap.set(message.id, id);
    return {
      ...rest,
      id,
      branchId,
      ...(parentId && idMap.has(parentId)
        ? { parentId: idMap.get(parentId)! }
        : {}),
    };
  });
  return {
    ...conversation,
    messages: [...conversation.messages, ...cloned],
    activeBranchId: branchId,
    updatedAt: Date.now(),
  };
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function base64ToBytes(value: string): Uint8Array {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
  const binary = atob(padded);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

export function encodeSharedConversation(
  conversation: Conversation,
  artifacts: Artifact[] = [],
  maxLength = 1_500_000,
): string {
  const payload: SharePayload = { conversation, artifacts };
  const base64 = bytesToBase64(new TextEncoder().encode(JSON.stringify(payload)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
  if (base64.length > maxLength) {
    throw new Error(
      "La conversazione è troppo grande per un link condivisibile. Esportala come JSON o Markdown.",
    );
  }
  return base64;
}

export function importSharedConversation(
  payload: string,
  createId: () => string = () => crypto.randomUUID(),
): SharedImport {
  const parsed: unknown = JSON.parse(
    new TextDecoder().decode(base64ToBytes(payload)),
  );
  if (!parsed || typeof parsed !== "object") throw new Error("Share non valido.");
  // Links minted before artifacts were shareable encode a bare conversation.
  const envelope = parsed as Partial<SharePayload>;
  const candidate = (
    envelope.conversation && typeof envelope.conversation === "object"
      ? envelope.conversation
      : parsed
  ) as Partial<Conversation>;
  const sharedArtifacts = Array.isArray(envelope.artifacts) ? envelope.artifacts : [];
  if (
    typeof candidate.id !== "string" ||
    typeof candidate.title !== "string" ||
    !Array.isArray(candidate.messages)
  ) {
    throw new Error("Share non valido.");
  }
  const now = Date.now();
  const defaultQuestionMode = normalizeQuestionMode();
  const branchMap = new Map<string, string>();
  const messageMap = new Map<string, string>();
  const messages = candidate.messages.map((message) => {
    if (
      !message ||
      typeof message !== "object" ||
      typeof message.id !== "string" ||
      typeof message.branchId !== "string" ||
      (message.role !== "user" && message.role !== "assistant") ||
      typeof message.content !== "string"
    ) {
      throw new Error("Messaggio condiviso non valido.");
    }
    if (messageMap.has(message.id)) {
      throw new Error("Share non valido: ID messaggio duplicato.");
    }
    const id = createId();
    messageMap.set(message.id, id);
    if (!branchMap.has(message.branchId)) branchMap.set(message.branchId, createId());
    return { ...message, id, branchId: branchMap.get(message.branchId)! };
  });
  // Artifact ids are remapped alongside messages so an imported copy can never
  // collide with, or overwrite, an artifact already in the local workspace.
  const artifactMap = new Map<string, string>();
  for (const artifact of sharedArtifacts) {
    if (artifact && typeof artifact.id === "string") {
      if (artifactMap.has(artifact.id)) {
        throw new Error("Share non valido: ID artifact duplicato.");
      }
      artifactMap.set(artifact.id, createId());
    }
  }
  const conversationId = createId();
  const remapMarkers = (content: string) =>
    content.replace(
      /\[\[artifact:([0-9a-fA-F-]{36})\]\]/g,
      (whole, id: string) => {
        const next = artifactMap.get(id);
        return next ? `[[artifact:${next}]]` : whole;
      },
    );

  const remappedMessages = messages.map((message, index) => {
    const original = candidate.messages![index]!;
    const { parentId: _parentId, artifactIds, ...rest } = message;
    const references = Array.isArray(artifactIds) ? artifactIds : undefined;
    return {
      ...rest,
      content: remapMarkers(message.content),
      ...(references
        ? {
            artifactIds: references.flatMap((id) => {
              const next = artifactMap.get(id);
              return next ? [next] : [];
            }),
          }
        : {}),
      ...(original.parentId && messageMap.has(original.parentId)
        ? { parentId: messageMap.get(original.parentId)! }
        : {}),
    };
  });
  const originalActive = candidate.activeBranchId;

  return {
    conversation: {
      id: conversationId,
      title: `${candidate.title} (condivisa)`,
      createdAt: typeof candidate.createdAt === "number" ? candidate.createdAt : now,
      updatedAt: now,
      prompt: typeof candidate.prompt === "string" ? candidate.prompt : "",
      messages: remappedMessages,
      activeBranchId:
        (originalActive && branchMap.get(originalActive)) ??
        remappedMessages[0]?.branchId ??
        createId(),
      tags: Array.isArray(candidate.tags)
        ? candidate.tags.filter((tag): tag is string => typeof tag === "string")
        : [],
      mode: isChatModeId(candidate.mode) ? candidate.mode : "standard",
      questionMode: normalizeQuestionMode(candidate.questionMode ?? defaultQuestionMode),
      messagesLoaded: true,
      shared: true,
    },
    artifacts: sharedArtifacts.flatMap((artifact): Artifact[] => {
      const id = artifactMap.get(artifact?.id ?? "");
      if (!id || typeof artifact.content !== "string") return [];
      const { messageId: originalMessageId, ...rest } = artifact;
      const messageId = originalMessageId && messageMap.get(originalMessageId);
      return [
        {
          ...rest,
          id,
          conversationId,
          revisions: Array.isArray(artifact.revisions) ? artifact.revisions : [],
          ...(messageId ? { messageId } : {}),
        },
      ];
    }),
  };
}
